/**
 * ogreclan.com, OgreClan's home: its welcome, OgreClan's own metagames (each with a write-up and resources, like
 * Smogon's: viability rankings, sample teams, guides), the guides (moved here from draft.ogreclan.com), an About Me
 * page, and a blog. Admins write it all, in Markdown (markdown.tsx), right on the pages; apps/api/src/home.ts keeps it.
 */
import { useEffect, useState } from 'preact/hooks';
import { api, signInUrl } from '../api';
import { MarkdownEditor, Markdown } from '../markdown';
import { useMe } from '../me';
import { Link, navigate } from '../router';
import { DISCORD_INVITE, DRAFT_ORIGIN, THEME_STORE, TOOLKIT_STORE } from '../sites';
import { ThemePicker } from '../theme';
import { ActionButton, ErrorBox, Loading, toast } from '../ui';
import { GuidesPage } from './guides';

const KINDS = [
	['viability-rankings', 'Viability Rankings'], ['sample-teams', 'Sample Teams'], ['guide', 'Guides'], ['other', 'Other Resources'],
] as const;
type Kind = typeof KINDS[number][0];

interface CustomFormat { id: string, name: string, desc: string, gameType: string, random: boolean, open: boolean, writeUp?: boolean, resources?: number }
interface Resource { id: string, kind: Kind, title: string, url: string, body: string, position: number }
interface PostSummary { slug: string, title: string, summary: string, author: string | null, published: boolean, publishedAt: string | null, updatedAt: string }
interface Post extends PostSummary { body: string, ownSummary: string }
interface Page { slug: string, title: string, body: string, updatedAt: string | null }

/** What an address gives, reloaded on asking */
function useApi<T>(path: string | null) {
	const [state, setState] = useState<{ data?: T, error?: unknown }>({});
	const [version, setVersion] = useState(0);
	useEffect(() => {
		if (!path) return;
		let current = true;
		setState({});
		api.get<T>(path).then(data => current && setState({ data }), error => current && setState({ error }));
		return () => {
			current = false;
		};
	}, [path, version]);
	return { ...state, reload: () => setVersion(v => v + 1) };
}

function isAdmin() {
	return !!useMe()?.user?.isAdmin;
}

function date(when: string | null) {
	return when ? new Date(when).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
}

/** "[Gen 9 Champions] Move Momentum" -> "Move Momentum" */
function shortName(name: string) {
	return name.replace(/^\[[^\]]*\]\s*/, '');
}

/* The frame */

function HomeHeader({ path }: { path: string }) {
	const me = useMe();
	const section = (prefix: string) => (prefix === '/' ? path === '/' : path === prefix || path.startsWith(`${prefix}/`)) ? 'active' : '';
	return <header class="top">
		<Link href="/" class="brand"><img src="/brand/logo-128.png" alt="OgreClan" />OgreClan</Link>
		<nav class="nav">
			<Link href="/formats" class={section('/formats')}>Metagames</Link>
			<Link href="/blog" class={section('/blog')}>Blog</Link>
			<Link href="/guides" class={section('/guides')}>Guides</Link>
			<Link href="/about" class={section('/about')}>About</Link>
			<a href={`${DRAFT_ORIGIN}/league`}>Draft Leagues</a>
			<a href="https://play.ogreclan.com">Play</a>
			<a href="/#extensions">Extensions</a>
			<a href={DISCORD_INVITE} target="_blank" rel="noopener">Discord</a>
		</nav>
		<span class="spacer" />
		<ThemePicker />
		{me?.user ? <a href={`${DRAFT_ORIGIN}/account`} class="who">
			{me.user.avatar && <img src={me.user.avatar} alt="" />}
			<span>{me.user.showdown?.name || me.user.name}</span>
		</a> : me && <a class="button primary small" href={signInUrl()}>Sign in with Discord</a>}
	</header>;
}

function NotFound() {
	return <div class="card"><h1>Page not found</h1><p>There's nothing at this address. <Link href="/">Go home</Link>.</p></div>;
}

/** ogreclan.com's header and the page an address shows */
export function HomeSite({ path }: { path: string }) {
	const parts = path.split('/').filter(Boolean).map(decodeURIComponent);
	let page;
	if (!parts.length) page = <HomeIndex />;
	else if (parts[0] === 'formats' && parts.length === 1) page = <FormatsPage />;
	else if (parts[0] === 'formats' && parts.length === 2) page = <FormatPage id={parts[1]!} />;
	else if (parts[0] === 'blog' && parts.length === 1) page = <BlogPage />;
	else if (parts[0] === 'blog' && parts[1] === 'new' && parts.length === 2) page = <PostEditor />;
	else if (parts[0] === 'blog' && parts.length === 2) page = <PostPage slug={parts[1]!} />;
	else if (parts[0] === 'blog' && parts[2] === 'edit' && parts.length === 3) page = <PostEditor slug={parts[1]} />;
	else if (parts[0] === 'about' && parts.length === 1) page = <TextPage slug="about" />;
	else if (parts[0] === 'guides' && parts.length <= 2) page = <GuidesPage section={parts[1] || 'start'} />;
	else page = <NotFound />;
	return <>
		<HomeHeader path={path} />
		<main>{page}</main>
	</>;
}

/* The home page */

function HomeIndex() {
	const intro = useApi<{ page: Page }>('/api/home/pages/home');
	const posts = useApi<{ posts: PostSummary[] }>('/api/home/posts');
	const formats = useApi<{ formats: CustomFormat[] }>('/api/home/formats');
	const latest = posts.data?.posts.filter(post => post.published).slice(0, 3) ?? [];
	// the header's link to the extensions (/#extensions): the sections above them fill in once their pages load, so it's
	// scrolled to then
	const settled = [intro, posts, formats].every(call => call.data || call.error);
	useEffect(() => {
		if (settled && location.hash === '#extensions') document.getElementById('extensions')?.scrollIntoView();
	}, [settled]);
	return <>
		<div class="card hero">
			<img src="/brand/logo-256.png" alt="OgreClan" />
			<div>
				<h1>{intro.data?.page.title || 'OgreClan'}</h1>
				{intro.data?.page.body ? <Markdown text={intro.data.page.body} /> : <p>OgreClan's own Pokémon metagames, draft leagues,
					and the guides to all of it.</p>}
				<p>
					<Link class="button primary" href="/formats">Our metagames</Link>{' '}
					<a class="button" href={`${DRAFT_ORIGIN}/league`}>Draft leagues</a>{' '}
					<a class="button" href="https://play.ogreclan.com">Play on Showdown</a>{' '}
					<a class="button" href={DISCORD_INVITE} target="_blank" rel="noopener">Join our Discord</a>
				</p>
				<EditText slug="home" page={intro.data?.page} onSaved={intro.reload} />
			</div>
		</div>
		{latest.length > 0 && <section>
			<h2>Latest from the blog</h2>
			<div class="grid">{latest.map(post => <PostCard key={post.slug} post={post} />)}</div>
			<p><Link href="/blog">Every post</Link></p>
		</section>}
		<section>
			<h2>Metagames</h2>
			{formats.error ? <ErrorBox error={formats.error} /> : !formats.data ? <Loading /> : <FormatCards formats={formats.data.formats.slice(0, 6)} />}
			<p><Link href="/formats">All of them</Link></p>
		</section>
		<section id="extensions">
			<h2>Our Chrome extensions</h2>
			<div class="grid">
				<a class="card ext-card" href={TOOLKIT_STORE} target="_blank" rel="noopener">
					<h3><img src="/masks/hearthflame.png" alt="" />Ogre Clan Toolkit for Pokémon Showdown</h3>
					<p>A clearer battle screen: each move's type, category, power and accuracy on its button, HP bars that show what
						this turn cost, and easier-to-read badges. Your division's colours and arenas, text sizes, keyboard shortcuts,
						and the Draft Planner beside your battle or teambuilder.</p>
					<p class="muted small">Free on the Chrome Web Store</p>
				</a>
				<a class="card ext-card" href={THEME_STORE} target="_blank" rel="noopener">
					<h3><img src="/masks/teal.png" alt="" />OgreClan Theme</h3>
					<p>Your division's colours (Teal, Wellspring, Hearthflame or Cornerstone, dark or light), Kitakami backgrounds and
						Ogerpon's masks on Showdown, Discord, WhatsApp Web and YouTube.</p>
					<p class="muted small">Free on the Chrome Web Store</p>
				</a>
			</div>
		</section>
	</>;
}

/* Metagames */

function FormatCards({ formats }: { formats: CustomFormat[] }) {
	if (!formats.length) return <p class="empty">No metagames to show right now.</p>;
	return <div class="grid">{formats.map(format => <Link key={format.id} href={`/formats/${format.id}`} class="card format-card">
		<h3>{shortName(format.name)}</h3>
		<p class="muted small">
			{format.name.match(/^\[([^\]]*)\]/)?.[1]}{format.gameType === 'doubles' ? ' · Doubles' : ''}{format.random ? ' · Random Battle' : ''}
			{!format.open && <span class="badge warn">Coming soon</span>}
		</p>
		<p>{format.desc.length > 220 ? `${format.desc.slice(0, 219).trimEnd()}…` : format.desc}</p>
		{!!format.resources && <p class="muted small">{format.resources} resource{format.resources === 1 ? '' : 's'}</p>}
	</Link>)}</div>;
}

function FormatsPage() {
	const formats = useApi<{ formats: CustomFormat[] }>('/api/home/formats');
	return <>
		<div class="card">
			<h1>OgreClan's metagames</h1>
			<p>Formats we made ourselves, all on Pokémon Champions mechanics. Play any of them on <a href="https://play.ogreclan.com">play.ogreclan.com</a>.
				Each has its own page with resources: viability rankings, sample teams and guides.</p>
		</div>
		{formats.error ? <ErrorBox error={formats.error} /> : !formats.data ? <Loading /> : <FormatCards formats={formats.data.formats} />}
	</>;
}

function FormatPage({ id }: { id: string }) {
	const admin = isAdmin();
	const page = useApi<{ format: CustomFormat, body: string, resources: Resource[] }>(`/api/home/formats/${encodeURIComponent(id)}`);
	const [editing, setEditing] = useState<string | null>(null);
	const [adding, setAdding] = useState(false);
	if (page.error) return <ErrorBox error={page.error} />;
	if (!page.data) return <Loading />;
	const { format, body, resources } = page.data;
	return <>
		<p><Link href="/formats">← Metagames</Link></p>
		<div class="card">
			<h1>{shortName(format.name)}</h1>
			<p class="muted small">{format.name}{format.random ? ' · Random Battle' : ''}{!format.open && ' · not open yet'}</p>
			<p>{format.desc}</p>
			{editing !== null ? <>
				<MarkdownEditor label="The write-up" value={editing} onInput={setEditing} />
				<p>
					<ActionButton class="primary" action={async () => {
						await api.put(`/api/home/formats/${format.id}`, { body: editing });
						setEditing(null);
						toast('Saved.', 'good');
						page.reload();
					}}>Save</ActionButton>{' '}
					<button type="button" onClick={() => setEditing(null)}>Cancel</button>
				</p>
			</> : <>
				{body && <Markdown text={body} />}
				{admin && <p><button type="button" class="small" onClick={() => setEditing(body)}>{body ? 'Edit the write-up' : 'Write about it'}</button></p>}
			</>}
			<p><a class="button primary" href="https://play.ogreclan.com">Play it on Showdown</a></p>
		</div>
		{KINDS.map(([kind, title]) => {
			const list = resources.filter(resource => resource.kind === kind);
			if (!list.length) return null;
			return <section key={kind} class="resources">
				<h2>{title}</h2>
				{list.map((resource, i) => <ResourceCard key={resource.id} resource={resource} admin={admin} first={i === 0}
					last={i === list.length - 1} onChange={page.reload} />)}
			</section>;
		})}
		{!resources.length && <p class="empty">No resources yet.{admin ? ' Add the first.' : ''}</p>}
		{admin && (adding ? <ResourceForm formatId={format.id} onDone={() => {
			setAdding(false);
			page.reload();
		}} /> : <p><button type="button" onClick={() => setAdding(true)}>Add a resource</button></p>)}
	</>;
}

function ResourceCard({ resource, admin, first, last, onChange }: { resource: Resource, admin: boolean, first: boolean, last: boolean, onChange: () => void }) {
	const [editing, setEditing] = useState(false);
	if (editing) {
		return <ResourceForm resource={resource} onDone={() => {
			setEditing(false);
			onChange();
		}} />;
	}
	const move = (direction: 'up' | 'down') => async () => {
		await api.put(`/api/home/resources/${resource.id}`, { move: direction });
		onChange();
	};
	return <div class="card resource">
		<h3>{resource.url ? <a href={resource.url} target="_blank" rel="noopener noreferrer">{resource.title}</a> : resource.title}</h3>
		{resource.body && <Markdown text={resource.body} />}
		{admin && <p class="resource-tools">
			<button type="button" class="small" onClick={() => setEditing(true)}>Edit</button>
			{!first && <ActionButton class="small" action={move('up')}>Move up</ActionButton>}
			{!last && <ActionButton class="small" action={move('down')}>Move down</ActionButton>}
			<ActionButton class="small danger" confirm={`Delete "${resource.title}"?`} action={async () => {
				await api.delete(`/api/home/resources/${resource.id}`);
				onChange();
			}}>Delete</ActionButton>
		</p>}
	</div>;
}

/** Adding a resource to a metagame, or changing one */
function ResourceForm({ formatId, resource, onDone }: { formatId?: string, resource?: Resource, onDone: () => void }) {
	const [kind, setKind] = useState<Kind>(resource?.kind ?? 'sample-teams');
	const [title, setTitle] = useState(resource?.title ?? '');
	const [url, setUrl] = useState(resource?.url ?? '');
	const [body, setBody] = useState(resource?.body ?? '');
	return <div class="card">
		<h2>{resource ? 'Change the resource' : 'Add a resource'}</h2>
		<div class="row">
			<label class="field"><span>Kind</span>
				<select value={kind} onChange={event => setKind((event.currentTarget as HTMLSelectElement).value as Kind)}>
					{KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
				</select>
			</label>
			<label class="field"><span>Title</span>
				<input value={title} maxLength={200} onInput={event => setTitle((event.currentTarget as HTMLInputElement).value)} />
			</label>
		</div>
		<label class="field"><span>Link <span class="muted">(optional: a thread, a sheet, …)</span></span>
			<input value={url} type="url" placeholder="https://" onInput={event => setUrl((event.currentTarget as HTMLInputElement).value)} />
		</label>
		<MarkdownEditor label={<>Text <span class="muted">(optional: Pokepaste links show their teams)</span></>} value={body} onInput={setBody} rows={10} />
		<p>
			<ActionButton class="primary" action={async () => {
				try {
					const values = { kind, title, url, body };
					if (resource) await api.put(`/api/home/resources/${resource.id}`, values);
					else await api.post(`/api/home/formats/${formatId}/resources`, values);
					toast('Saved.', 'good');
					onDone();
				} catch (e) {
					toast((e as Error).message, 'bad');
				}
			}}>{resource ? 'Save' : 'Add it'}</ActionButton>{' '}
			<button type="button" onClick={onDone}>Cancel</button>
		</p>
	</div>;
}

/* The blog */

function PostCard({ post }: { post: PostSummary }) {
	return <Link href={`/blog/${post.slug}`} class="card post-card">
		<h3>{post.title}{!post.published && <span class="badge warn">Draft</span>}</h3>
		<p class="muted small">{post.published ? date(post.publishedAt) : `Last changed ${date(post.updatedAt)}`}{post.author && ` · ${post.author}`}</p>
		<p>{post.summary}</p>
	</Link>;
}

function BlogPage() {
	const admin = isAdmin();
	const posts = useApi<{ posts: PostSummary[] }>('/api/home/posts');
	return <>
		<div class="card">
			<h1>Blog</h1>
			<p>News, league write-ups, and thoughts on our metagames.</p>
			{admin && <p><Link class="button primary" href="/blog/new">Write a post</Link></p>}
		</div>
		{posts.error ? <ErrorBox error={posts.error} /> : !posts.data ? <Loading /> : !posts.data.posts.length ?
			<p class="empty">No posts yet.</p> : <div class="stack">{posts.data.posts.map(post => <PostCard key={post.slug} post={post} />)}</div>}
	</>;
}

function PostPage({ slug }: { slug: string }) {
	const admin = isAdmin();
	const found = useApi<{ post: Post }>(`/api/home/posts/${encodeURIComponent(slug)}`);
	useEffect(() => {
		if (found.data) document.title = `${found.data.post.title} - OgreClan`;
		return () => {
			document.title = 'OgreClan';
		};
	}, [found.data]);
	if (found.error) return <ErrorBox error={found.error} />;
	if (!found.data) return <Loading />;
	const { post } = found.data;
	return <>
		<p><Link href="/blog">← Blog</Link></p>
		<article class="card post">
			<h1>{post.title}{!post.published && <span class="badge warn">Draft</span>}</h1>
			<p class="muted small">{post.published ? date(post.publishedAt) : 'Not published yet'}{post.author && ` · ${post.author}`}</p>
			<Markdown text={post.body} />
			{admin && <p class="resource-tools">
				<Link class="button small" href={`/blog/${post.slug}/edit`}>Edit</Link>
				<ActionButton class="small" action={async () => {
					await api.put(`/api/home/posts/${post.slug}`, { published: !post.published });
					found.reload();
				}}>{post.published ? 'Unpublish' : 'Publish'}</ActionButton>
				<ActionButton class="small danger" confirm={`Delete "${post.title}"? This can't be undone.`} action={async () => {
					await api.delete(`/api/home/posts/${post.slug}`);
					toast('Deleted.', 'good');
					navigate('/blog');
				}}>Delete</ActionButton>
			</p>}
		</article>
	</>;
}

/** Writing a new post, or changing one */
function PostEditor({ slug }: { slug?: string }) {
	const admin = isAdmin();
	const existing = useApi<{ post: Post }>(slug ? `/api/home/posts/${encodeURIComponent(slug)}` : null);
	const [form, setForm] = useState<{ title: string, slug: string, summary: string, body: string, published: boolean } | null>(slug ? null : {
		title: '', slug: '', summary: '', body: '', published: false,
	});
	useEffect(() => {
		const post = existing.data?.post;
		if (post) setForm({ title: post.title, slug: post.slug, summary: post.ownSummary, body: post.body, published: post.published });
	}, [existing.data]);
	if (!admin) return <div class="card"><h1>Only admins write posts</h1><p><Link href="/blog">Read the blog</Link></p></div>;
	if (existing.error) return <ErrorBox error={existing.error} />;
	if (!form) return <Loading />;
	const set = (key: keyof typeof form) => (event: Event) => setForm({ ...form, [key]: (event.currentTarget as HTMLInputElement).value });
	return <div class="card">
		<h1>{slug ? 'Change the post' : 'Write a post'}</h1>
		<label class="field"><span>Title</span><input value={form.title} maxLength={200} onInput={set('title')} /></label>
		<div class="row">
			<label class="field"><span>Address <span class="muted">(ogreclan.com/blog/…; from the title if you leave it out)</span></span>
				<input value={form.slug} maxLength={80} placeholder="my-first-post" onInput={set('slug')} /></label>
			<label class="field"><span>Summary <span class="muted">(optional: for the list and link previews)</span></span>
				<input value={form.summary} maxLength={500} onInput={set('summary')} /></label>
		</div>
		<MarkdownEditor label="The post" value={form.body} rows={22} onInput={body => setForm({ ...form, body })} />
		<label class="checkbox"><input type="checkbox" checked={form.published}
			onChange={event => setForm({ ...form, published: (event.currentTarget as HTMLInputElement).checked })} /> Published (unticked, it's a draft only admins see)</label>
		<p>
			<ActionButton class="primary" action={async () => {
				try {
					const values = { ...form, slug: form.slug.trim() || undefined };
					const saved = slug ? await api.put<{ post: Post }>(`/api/home/posts/${slug}`, values) :
						await api.post<{ post: Post }>('/api/home/posts', values);
					toast('Saved.', 'good');
					navigate(`/blog/${saved.post.slug}`);
				} catch (e) {
					toast((e as Error).message, 'bad');
				}
			}}>Save</ActionButton>{' '}
			<Link class="button" href={slug ? `/blog/${slug}` : '/blog'}>Cancel</Link>
		</p>
	</div>;
}

/* About Me, and the home page's own words */

function EditText({ slug, page, onSaved }: { slug: string, page?: Page, onSaved: () => void }) {
	const admin = isAdmin();
	const [form, setForm] = useState<{ title: string, body: string } | null>(null);
	if (!admin || !page) return null;
	if (!form) return <p><button type="button" class="small" onClick={() => setForm({ title: page.title, body: page.body })}>Edit this</button></p>;
	return <div class="edit-text">
		<label class="field"><span>Title</span><input value={form.title} maxLength={200}
			onInput={event => setForm({ ...form, title: (event.currentTarget as HTMLInputElement).value })} /></label>
		<MarkdownEditor value={form.body} onInput={body => setForm({ ...form, body })} />
		<p>
			<ActionButton class="primary" action={async () => {
				try {
					await api.put(`/api/home/pages/${slug}`, form);
					setForm(null);
					toast('Saved.', 'good');
					onSaved();
				} catch (e) {
					toast((e as Error).message, 'bad');
				}
			}}>Save</ActionButton>{' '}
			<button type="button" onClick={() => setForm(null)}>Cancel</button>
		</p>
	</div>;
}

function TextPage({ slug }: { slug: string }) {
	const found = useApi<{ page: Page }>(`/api/home/pages/${slug}`);
	if (found.error) return <ErrorBox error={found.error} />;
	if (!found.data) return <Loading />;
	const { page } = found.data;
	return <div class="card">
		<h1>{page.title}</h1>
		{page.body ? <Markdown text={page.body} /> : <p class="muted">Nothing here yet.</p>}
		<EditText slug={slug} page={page} onSaved={found.reload} />
	</div>;
}
